import { FormKit } from "@formkit/vue"
import { Filterable, RawArgDefOptions, ClientFuncArgBag } from "src/components/ReportBuilder/ReportBuilder.io"
import { UiOption, assertTruthy, exhaustiveCaseGuard, requireNonNull, unreachable, vReqT } from "src/helpers/utils"
import { computed, defineComponent, ref, watch } from "vue"
import { UiQNode, NodeType } from "./UiQNode"
import { MungedArgDefOptions, containsAnyRedundantDescendantNodes, fixupAllRedundantNodes, FilterableFormData, ClientFilterableFormData, createClientFuncArgBag, defaultValsForArgsDef } from "./ClientQueryData"
import { User } from "src/store/User"
import { Btn2 } from "../UserInterface/Btn2"
import { ArgFormElement } from "./ReportBuilder.argFormElement"

const propsDef = {
  node: vReqT<UiQNode>(),
  getters: vReqT<{
    getFilterablesOptions: () => UiOption[],
    getFilterableDefOrFail: (id: string) => Filterable,
    optionsMunger: (opts: RawArgDefOptions) => MungedArgDefOptions,
  }>(),
} as const;

const debugPredicateValues = ref(false);

/**
 * This export wraps the impl with the sole intent to use the correct render keys
 */
export const QueryNodeElement = defineComponent({
  props: propsDef,
  setup(props) {
    // renderKey is by (parent.nodeID, nodeID) because nodes can shift across parents, and when doing so should re-render
    const renderKey = computed(() => `${props.node.parent?.nodeID ?? "<root>"}/${props.node.nodeID}`)
    return () => <QueryNodeElementImpl key={renderKey.value} {...props}/>
  }
});

const QueryNodeElementImpl = defineComponent({
  props: propsDef,
  setup(props) {
    const maybeGetCurrentlySelectedFilterableID = () : string => {
      if (props.node.type === NodeType.predicate) {
        if (props.node.data === null) {
          return ""
        }
        return props.node.data.filterableDef.name;
      }
      else {
        return "";
      }
    }

    const selectedFilterableID = ref(maybeGetCurrentlySelectedFilterableID())
    const leafRenderKey = ref(0)

    //
    // We are responsible here for initializing a node's data based on the `selectedFilterableID` in response to form changes.
    // Note that `selectedFilterableID` is a local ref initialized __from__ the node's data on first mount.
    // This is not an immediate watch -- if node data was initialized at time of mount, then that's fine, we use that,
    // and we'll have initialized to the correct `selectedFilterableID`. When `selectedFilterableID` changes, we update the node's
    // data. If we end up remounting (node parent changes or etc.) then the data comes along for the ride and we repeat.
    //
    watch(() => selectedFilterableID.value, () => {
      if (props.node.type !== NodeType.predicate) {
        // It does not make sense to do this for other node types.
        // UI for changing `selectedFilterableID` should only be exposed for predicate node types.
        throw Error("illegal state")
      }

      leafRenderKey.value += 1;

      if (selectedFilterableID.value === "") {
        props.node.data = null;
      }
      else {
        // on change of selected filterable, update the node's values to be reasonable defaults,
        const filterableDef = props.getters.getFilterableDefOrFail(selectedFilterableID.value)
        props.node.data = FilterableFormData(
          filterableDef,
          null,
          props.getters.optionsMunger
        );
      }
    })

    return () => {
      if (props.node.type === NodeType.predicate) {
        const node = props.node;
        return (
          <div style="--fk-margin-outer: none;">
            {props.node.changeableTarget
              ? <FormKit
                type="select"
                options={props.getters.getFilterablesOptions()}
                v-model={selectedFilterableID.value}
                name="Field"
                validation={[["required"]]}
              />
              : props.getters.getFilterablesOptions().find(v => v.value === selectedFilterableID.value)?.label
            }
            {
              node.data
                ? (
                  <FormKit type="group">
                    {
                      debugPredicateValues.value
                        ? <pre class="text-xs my-2 shadow-md p-2 rounded-md bg-black" style="color: rgb(255,225,0);">{node.data.filterableDef.name} = {JSON.stringify(node.data.values, null, 2)}</pre>
                        : null
                    }
                    <FilterableFormElement
                      key={leafRenderKey.value}
                      formData={node.data}
                      optionsMunger={props.getters.optionsMunger}
                    />
                  </FormKit>
                )
                : null
            }
            {props.node.deleteable
              ? <div class="my-2 flex gap-2 items-start">
                <Btn2 class="px-2 py-1" onClick={() => {
                  node.deleteSelfFromParentChildrenList();

                  //
                  // climb node tree and delete all elements that have become empty due this deletion
                  //
                  let workingNode = node.parent;

                  while (true) {
                    if (workingNode.children.length > 0) {
                      // has children, we're done
                      break;
                    }
                    else {
                      if (workingNode.parent) {
                        workingNode.deleteSelfFromParentChildrenList();
                        workingNode = workingNode.parent;
                      }
                      else {
                        // no parent to ascend to, workingNode has become root, we're done
                        break;
                      }
                    }
                  }

                  // if we ascended to root, we might have deleted the last child from root;
                  // in which case, we should push a fresh child so the UI is not totally empty.
                  if (workingNode.parent === null) {
                    const treeRoot = workingNode
                    if (treeRoot.children.length === 0) {
                      treeRoot.pushFreshPredicate();
                    }
                  }
                }}>Delete</Btn2>
              </div>
              : null}
          </div>
        )
      }
      else {
        const node = props.node;
        const isRootNode = node.parent === null;
        return (
          <div>
            <div class="border rounded-md shadow-md" style={`background-color: ${node.which === "and" ? 'white' : `rgb(245,255,255)`}`}>
              <div class="p-1 mb-2 text-xs bg-green-700 text-black rounded-t-md flex items-center">
                <select class="py-1 pl-1 text-xs rounded-md" v-model={props.node.which} disabled={!props.node.mutableWhich}>
                  <option value="and">And</option>
                  <option value="or">Or</option>
                </select>
                <div class="ml-auto">
                  {
                    // if we're root, and we contain any redundant nodes at all, offer to clean them all up
                    // This can take 100s of ms for vue to rebuild the dom for typical sized queries, we might want to look into that.
                    // But also we don't expect to do it too often.
                    props.node.mutableWhich && isRootNode && containsAnyRedundantDescendantNodes(node)
                      ? <div class="text-white underline cursor-pointer" onClick={() => fixupAllRedundantNodes(node)}>Cleanup all redundant ands/ors</div>
                      : null
                  }
                  {
                    isRootNode && User.isInleague
                      ? <label class="flex text-xs gap-2 text-white my-1">
                          <input type="checkbox" v-model={debugPredicateValues.value}/>
                          Debug predicate values
                        </label>
                      : null
                  }
                  {
                    props.node.mutableWhich && node.parent?.which === node.which
                      ? <div class="text-white underline cursor-pointer" onClick={() => node.mergeWithParentIfRedundant()}>Merge with outer '{node.parent.which}'</div>
                      : node.parent && node.children.length <= 1
                      ? <div class="text-white underline cursor-pointer" onClick={() => node.mergeWithParentIfRedundant()}>Merge redundant single node with outer '{node.parent.which}'</div>
                      : null
                  }
                </div>
              </div>
              {
                props
                  .node
                  .children
                  .map((child, i, a) => {
                    const isLast = i === a.length - 1
                    return (
                      <div class="border-black">
                        <div class="mx-3" style="--fk-bg-input: white;">
                          <FormKit type="group">
                            <QueryNodeElement
                              node={child}
                              getters={props.getters}
                            />
                          </FormKit>
                          {
                            // if we're not the last element in our parent's list, show how we will
                            // connect to the next element in the parent's list
                            isLast
                              ? null
                              : (
                                <div class="flex items-center my-1 gap-2">
                                  <span style="flex-grow: 1;">
                                    <div class="border-b border-gray-300" style="height:50%; width:100%; top:0;left:0;"/>
                                  </span>
                                  <span class="">{child.parent?.which}</span>
                                  <span style="flex-grow: 20;">
                                    <div class="border-b border-gray-300" style="height:50%; width:100%; top:0;left:0;"/>
                                  </span>
                                </div>
                              )
                          }
                        </div>
                        {
                          isLast
                            ? (
                              <div class="my-2 border-t border-dashed border-gray-300 flex">
                                <div class="pl-2 pb-0 pt-2 flex gap-2 items-start">
                                  <Btn2 type="button" style="padding: .35em .45em;" onClick={() => node.pushFreshPredicate()}>
                                    {
                                      node.which === "and"
                                        ? "+And"
                                        : "+Or"
                                    }
                                  </Btn2>
                                </div>
                                <div class="px-2 pb-0 pt-2 flex gap-2 items-start">
                                  <Btn2 type="button" style="padding: .35em .45em;" onClick={() => {
                                    const type = node.which === "and" ? "or" : "and";
                                    const c = node.pushFreshConnective(type)
                                    c.pushFreshPredicate();
                                    c.pushFreshPredicate();
                                  }}>
                                    <div class="flex items-center gap-1">
                                      <span>{node.which === "or" ? "+And" : "+Or"}</span>
                                      <span class="text-xs">(subclause)</span>
                                    </div>
                                  </Btn2>
                                </div>
                              </div>
                            )
                            : null
                        }
                      </div>
                    )
                  })
                }
            </div>
          </div>
        )
      }
    }
  }
})

const FilterableFormElement = defineComponent({
  props: {
    formData: vReqT<ClientFilterableFormData>(),
    optionsMunger: vReqT<(opts: RawArgDefOptions) => MungedArgDefOptions>(),
  },
  setup(props) {
    const filterableDef = computed(() => props.formData.filterableDef)

    const handleValueUpdate = () => {
      props.formData.clientFuncArgBag = createClientFuncArgBag(filterableDef.value, props.formData.values, props.optionsMunger)

      for (let i = 0; i < filterableDef.value.argsDef.length; i++) {
        const argDef = filterableDef.value.argsDef[i]
        const value = props.formData.values[i]
        assertTruthy(argDef.name === value.name)

        if (argDef.if) {
          const oldIf : boolean = value.value.satisfiesIf
          const newIf : boolean = argDef.if(props.formData.clientFuncArgBag)

          if (oldIf === false && newIf === true) {
            value.value = defaultValsForArgsDef(argDef, null, props.optionsMunger)
            assertTruthy(value.value.satisfiesIf) // we assume `defaultValsForArgsDef` sets this to true
          }
          else if (oldIf === true && newIf === false) {
            value.value.satisfiesIf = false
            value.value.value = null
          }
          else {
            // no change to if result
          }
        }
        else {
          // There's no if, meaning it will never be if'd away.
          // Assert this fact.
          assertTruthy(value.value.satisfiesIf)
        }
      }
    }

    return () => {
      if (filterableDef.value.type === "functionlike") {
        return (
          <div>
            <div class="ml-1 text-sm my-2">{filterableDef.value.tooltip}</div>
            {
              filterableDef.value.argsDef.map((def,i) => {
                if (!props.formData.values[i].value.satisfiesIf) {
                  return null
                }

                return <div class="my-2">
                  <ArgFormElement
                    type={def.type}
                    options_calleeDefensivelyCopies={def.options ? props.optionsMunger(def.options) : null}
                    label={def.label}
                    value={props.formData.values[i].value}
                    onDidUpdate={() => handleValueUpdate()}
                  />
                </div>
              })
            }
          </div>
        )
      }
      else {
        unreachable()
      }
    }
  }
})

/**
 * debug helper, to turn cyclic `parent` refs into just their nodeIDs
 */
export function stringifyNode(v: UiQNode) : string {
  return JSON.stringify(decycle(v), null, 2)

  function decycle(v: UiQNode) : object {
    if (v.type === NodeType.connective) {
      return {
        ...v,
        parent: v.parent?.nodeID ?? null,
        children: v.children.map(decycle)
      }
    }
    else {
      return {
        ...v,
        parent: v.parent?.nodeID ?? null
      }
    }
  }
}

/**
 * similar to `stringifyQueryForUI` but returns an HTML tree instead of a string
 */
export const QueryAsDomTree = defineComponent({
  props: {
    node: vReqT<UiQNode>(),
    optionsMunger: vReqT<(opts: RawArgDefOptions) => MungedArgDefOptions>(),
  },
  setup(props) {
    const hasMouseFocus = ref(false);

    // TODO: this is also a computed in a few other places (at this time at least in the "filterable form element"),
    // which is needless extra work; maybe we can store it as a computed on the UiQNode, or pass it in as a prop or something.
    const clientFuncArgBag = computed(() => {
      if (props.node.type === NodeType.predicate && props.node.data) {
        return createClientFuncArgBag(props.node.data.filterableDef, props.node.data.values, props.optionsMunger)
      }
      else {
        return null
      }
    })

    return () => {
      if (props.node.type === NodeType.predicate) {
        if (props.node.data === null) {
          return <div>?</div>
        }

        return <div>{evaluateToStringExpr(props.node.data.filterableDef, requireNonNull(clientFuncArgBag.value)) ?? "?"}</div>
      }
      else if (props.node.type === NodeType.connective) {
        const which = props.node.which;
        return (
          <div>
            <div style="display:grid; grid-template-columns: 12px 1fr; grid-auto-flow: column;">
              <div style="position:relative;">
                <div
                  style="height:100%; width:50%; top:0; right:0; position:absolute;"
                  class={`border-l ${hasMouseFocus.value ? `border-slate-600 bg-gray-100` : `border-slate-200 bg-white`}`}
                />
              </div>
              <div
                onMouseout={(e) => { e.stopPropagation(); hasMouseFocus.value = false; }}
                onMouseover={(e) => { e.stopPropagation(); hasMouseFocus.value = true; }}
                class={`${hasMouseFocus.value ? "bg-gray-100" : "bg-white"} p-1`}
              >
                {
                  props.node.children.map((child, i, a) => {
                    const isLast = i === a.length - 1;
                    return (
                      <>
                        <QueryAsDomTree node={child} optionsMunger={props.optionsMunger}/>
                        {
                          isLast
                            ? null
                            : which
                        }
                      </>
                    )
                  })
                }
              </div>
            </div>
          </div>
        )
      }
      else {
        exhaustiveCaseGuard(props.node)
      }
    }
  }
});

/**
 * render template format is like
 * "text {argName.label} {argName.value} {argName2.value} and so on"
 * where the interpolations always have the form '\w+.(label|value)' and where "label" only makes sense for arguments fed by an options list.
 */
function evaluateToStringExpr(
  filterableDef: Filterable,
  argbag: ClientFuncArgBag
) : string {
  if (filterableDef.getAsString) {
    try {
      return filterableDef.getAsString(filterableDef, argbag);
    }
    catch {
      return "(stringification did not complete succesfully)"
    }
  }
  else {
    //
    // this is an ugly default for cases where there is no "getAsString" function
    //
    return `>>> ${filterableDef.label} = ${JSON.stringify(argbag)}`
  }
}
